import { createClient, SupabaseClient } from '@supabase/supabase-js';

// Default project configuration (can be populated via .env or updated in Settings)
export const DEFAULT_SUPABASE_URL =
  import.meta.env.VITE_SUPABASE_URL || 'https://lingoping.supabase.co';

export const DEFAULT_SUPABASE_KEY =
  import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ||
  import.meta.env.VITE_SUPABASE_ANON_KEY ||
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.e30.placeholder';

export const DEFAULT_SUPABASE_ANON_KEY = DEFAULT_SUPABASE_KEY;

let supabaseInstance: SupabaseClient | null = null;

/**
 * Storage adapter that persists Supabase auth session in chrome.storage.local
 */
const chromeStorageAdapter = {
  getItem: async (key: string): Promise<string | null> => {
    if (typeof chrome !== 'undefined' && chrome.storage?.local) {
      const res = await chrome.storage.local.get(key);
      return (res[key] as string) || null;
    }
    if (typeof window !== 'undefined' && window.localStorage) {
      return window.localStorage.getItem(key);
    }
    return null;
  },
  setItem: async (key: string, value: string): Promise<void> => {
    if (typeof chrome !== 'undefined' && chrome.storage?.local) {
      await chrome.storage.local.set({ [key]: value });
      return;
    }
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(key, value);
    }
  },
  removeItem: async (key: string): Promise<void> => {
    if (typeof chrome !== 'undefined' && chrome.storage?.local) {
      await chrome.storage.local.remove(key);
      return;
    }
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.removeItem(key);
    }
  },
};

/** Where the session is kept and how the sign-in comes back; the phone app replaces these. */
export interface SupabaseAuthOptions {
  storage: typeof chromeStorageAdapter;
  /** Read the session from the page's URL after a redirect (websites only). */
  detectSessionInUrl: boolean;
  flowType: 'pkce' | 'implicit';
}

let authOverrides: Partial<SupabaseAuthOptions> = {};

/**
 * Lets a platform without chrome.storage or a page URL (the phone app) give the client its own
 * session storage and sign-in flow. Call once at start-up, before the first getSupabase().
 */
export function configureSupabaseAuth(options: Partial<SupabaseAuthOptions>): void {
  authOverrides = options;
  supabaseInstance = null;
}

/**
 * Returns or initializes the global Supabase client
 */
export function getSupabase(url?: string, anonKey?: string): SupabaseClient {
  const targetUrl = url || DEFAULT_SUPABASE_URL;
  const targetKey = anonKey || DEFAULT_SUPABASE_ANON_KEY;

  if (
    supabaseInstance &&
    (!url || url === DEFAULT_SUPABASE_URL) &&
    (!anonKey || anonKey === DEFAULT_SUPABASE_ANON_KEY)
  ) {
    return supabaseInstance;
  }

  // The extension signs in through chrome.identity and reads the tokens itself. On a website the
  // Google redirect lands back on the page, so supabase-js has to read the session from the URL
  // (PKCE: `?code=`, which it removes again).
  const onWebsite = typeof chrome === 'undefined' || !chrome.identity;

  supabaseInstance = createClient(targetUrl, targetKey, {
    auth: {
      storage: chromeStorageAdapter,
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: onWebsite,
      ...(onWebsite ? { flowType: 'pkce' as const } : {}),
      ...authOverrides,
    },
  });

  return supabaseInstance;
}

/**
 * Checks whether valid Supabase credentials have been configured
 */
export function isSupabaseConfigured(url?: string, anonKey?: string): boolean {
  const targetUrl = url || DEFAULT_SUPABASE_URL;
  const targetKey = anonKey || DEFAULT_SUPABASE_ANON_KEY;
  return (
    Boolean(targetUrl) &&
    !targetUrl.includes('placeholder') &&
    Boolean(targetKey) &&
    !targetKey.includes('placeholder')
  );
}
